import type { AuthRedirect } from '@dcs/platform/ports';
import type { ProgressTransport } from '@dcs/platform/progress';
import { createWebAuthRedirect } from '@dcs/platform/web';
import { QueryClientContext, type QueryClient } from '@tanstack/react-query';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { purgeAttemptQueue } from '../../features/history/save/save-registry';
import {
  cancelProgressSignOut,
  createApiProgressTransport,
  finishProgressSignOut,
  prepareProgressSignOut,
  useProgressSync,
} from '../../features/history/sync';
import {
  loadAuthClient,
  type AuthClientLike,
  type LoadAuthClient,
  type SessionSnapshot,
} from './auth-client';

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
}

/** `loading` until the client chunk and the first `get-session` answer are in. */
export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** Return paths and social sign-in callbacks (platform port). */
  redirect: AuthRedirect;
  /** The Better Auth client (loaded on first call, then the same one). */
  getClient(): Promise<AuthClientLike>;
  /**
   * `true` once signed out; the session store then reports `anonymous` by itself. This explicit
   * sign-out purges the account's study progress and queued attempts from this browser.
   */
  signOut(): Promise<boolean>;
  /**
   * Asks the server for the session again (after the account was deleted: the cookie is gone, so
   * the store turns `anonymous`). Never rejects.
   */
  refreshSession(): Promise<void>;
}

/** Route of the sign-in screen; `?next=` carries where to go back to. */
export const SIGN_IN_PATH = '/dang-nhap';

/** Link to the sign-in screen that returns to `from` (path + query + hash of the current page). */
export function signInHref(from: string): string {
  return from === '/' || from === ''
    ? SIGN_IN_PATH
    : `${SIGN_IN_PATH}?next=${encodeURIComponent(from)}`;
}

const AuthContext = createContext<AuthState | null>(null);

const noSubscription = () => () => undefined;
const noSnapshot = () => null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The signed-in user of a session snapshot, or `null` when there is none. The payload comes off
 * the network: without the API (e.g. `vite preview`, whose SPA fallback answers `get-session`
 * with `index.html`) `data` can be a string, `{}` or a user without an id, so it is checked
 * rather than trusted to match {@link SessionSnapshot}.
 */
export function sessionUserOf(snapshot: SessionSnapshot | null): AuthUser | null {
  const data: unknown = snapshot?.data;
  if (!isRecord(data)) return null;
  const user = data['user'];
  if (!isRecord(user)) return null;
  const { id, email, name } = user;
  if (typeof id !== 'string' || id === '' || typeof email !== 'string') return null;
  return { id, email, name: typeof name === 'string' ? name : null };
}

/** Who a session snapshot says is signed in: `undefined` while it loads. */
const identityOf = (snapshot: SessionSnapshot): string | null | undefined =>
  snapshot.isPending ? undefined : (sessionUserOf(snapshot)?.id ?? null);

/**
 * Current session of a client (Better Auth `useSession` store), `null` while the client loads.
 *
 * Server state cache (F6 T-2.3/T-2.5): whenever the signed-in user changes (sign-out, another
 * account, a session that ended), the TanStack QueryClient is cleared in the store's listener, so
 * before React renders the new user nothing of the previous one is left to show and every screen
 * fetches again for the new one. Clearing later, in an effect, would also cancel the fetches the
 * new user's screens had just started.
 */
function useSessionSnapshot(
  client: AuthClientLike | null,
  queryClient: QueryClient | undefined,
): SessionSnapshot | null {
  const identity = useRef<string | null | undefined>(undefined);
  const subscribe = useCallback(
    (onChange: () => void) =>
      client
        ? client.useSession.subscribe((value) => {
            const next = identityOf(value);
            if (next !== undefined) {
              const previous = identity.current;
              identity.current = next;
              if (previous !== undefined && previous !== next) queryClient?.clear();
            }
            onChange();
          })
        : noSubscription(),
    [client, queryClient],
  );
  const getSnapshot = useCallback(() => (client ? client.useSession.get() : null), [client]);
  return useSyncExternalStore(subscribe, client ? getSnapshot : noSnapshot, noSnapshot);
}

interface AuthProviderProps {
  children: ReactNode;
  /** Defaults to the lazy Better Auth client; tests pass a fake. */
  loadClient?: LoadAuthClient;
  /** Defaults to the web adapter on the page origin. */
  redirect?: AuthRedirect;
  /**
   * Server side of the progress sync, made once per sign-in. Defaults to the `@dcs/api` client,
   * imported lazily (never part of the `/ly-thuyet` entry chunk).
   */
  progressTransport?: () => ProgressTransport;
}

const apiProgressTransport = (): ProgressTransport =>
  createApiProgressTransport(() => import('@dcs/api'));

/**
 * Session of the signed-in user, from the Better Auth client's session store (it reads the HttpOnly
 * cookie through `/api/auth/get-session`; nothing here touches cookies). The client is loaded after the
 * first render, so the shell and `/ly-thuyet` do not carry it; until then the status is `loading`.
 * A client that cannot load (offline on a first visit) leaves the user `anonymous`.
 *
 * Also runs the study-progress sync of the signed-in user (F6 T-2.4): on while `authenticated`,
 * stopped once `anonymous` (`signOut` purges the account's progress from this browser; a session
 * that ended by itself keeps a 7-day stash of the unsent changes for that user), left alone while
 * `loading`.
 */
export function AuthProvider({
  children,
  loadClient = loadAuthClient,
  redirect,
  progressTransport = apiProgressTransport,
}: AuthProviderProps) {
  const [authRedirect] = useState(
    () => redirect ?? createWebAuthRedirect({ signInPath: SIGN_IN_PATH }),
  );
  const [client, setClient] = useState<AuthClientLike | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const pending = useRef<Promise<AuthClientLike> | null>(null);

  const getClient = useCallback(() => {
    pending.current ??= loadClient().catch((error: unknown) => {
      pending.current = null;
      throw error;
    });
    return pending.current;
  }, [loadClient]);

  useEffect(() => {
    let active = true;
    getClient().then(
      (loaded) => {
        // The Better Auth client is a Proxy over a function: passed as is, React would call it as a
        // state updater (an HTTP request) and store the promise it returns.
        if (active) setClient(() => loaded);
      },
      () => {
        if (active) setLoadFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [getClient]);

  // Optional: a test may mount the provider without a QueryClientProvider.
  const queryClient = use(QueryClientContext);
  const snapshot = useSessionSnapshot(client, queryClient);
  const sessionUser = sessionUserOf(snapshot);
  const status: AuthStatus =
    snapshot === null
      ? loadFailed
        ? 'anonymous'
        : 'loading'
      : snapshot.isPending
        ? 'loading'
        : sessionUser
          ? 'authenticated'
          : 'anonymous';
  const userId = sessionUser?.id;
  const userEmail = sessionUser?.email;
  const userName = sessionUser?.name ?? null;

  useProgressSync(
    status === 'loading' ? undefined : status === 'authenticated' ? (userId ?? null) : null,
    progressTransport,
  );

  const signedInId = status === 'authenticated' ? userId : undefined;

  const signOut = useCallback(async () => {
    const leaving = signedInId;
    // While the session still works: one last progress flush (30 s spacing kept), and mark the
    // coming session end as explicit so the account's progress is purged, not stashed.
    await prepareProgressSignOut();
    try {
      const { error } = await (await getClient()).signOut();
      if (error === null) {
        finishProgressSignOut();
        // Explicit sign-out only (a session that merely ended keeps the queue for the next sign-in,
        // T-2.2 M-a): the account's unsent results, dead letters and owner hint leave this browser.
        if (leaving !== undefined) purgeAttemptQueue(leaving);
        return true;
      }
    } catch {
      // Same as a refused sign-out below.
    }
    cancelProgressSignOut();
    return false;
  }, [getClient, signedInId]);

  const refreshSession = useCallback(async () => {
    try {
      await (await getClient()).useSession.get().refetch?.();
    } catch {
      // The store keeps its value; it refetches by itself on focus.
    }
  }, [getClient]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user:
        userId !== undefined && userEmail !== undefined
          ? { id: userId, email: userEmail, name: userName }
          : null,
      redirect: authRedirect,
      getClient,
      signOut,
      refreshSession,
    }),
    [status, userId, userEmail, userName, authRedirect, getClient, signOut, refreshSession],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const auth = use(AuthContext);
  if (!auth) throw new Error('useAuth must be used inside <AuthProvider>');
  return auth;
}
